import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import { Plus, ShieldCheck, Pencil } from "lucide-react";

// GL responses may come back as an array OR wrapped in {data:[]} / {roles:[]}.
const normalizeList = (d) => (Array.isArray(d) ? d : d?.data || d?.roles || []);

export default function RolesPermissionsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // TY-33: track in-flight request so we can abort a superseded roles fetch
  // and drop stale responses if a newer org has been selected in the meantime.
  const inflightRef = useRef({ controller: null, orgId: "" });

  // Load orgs and honor ?org_id=... from the URL (TY-35). Falls back to the
  // first org when the param is missing OR points to an unknown org id.
  useEffect(() => {
    API.get("/organizations")
      .then(({ data }) => {
        const list = data || [];
        setOrgs(list);
        const urlOrg = searchParams.get("org_id");
        const valid = urlOrg && list.some((o) => o.id === urlOrg);
        const initial = valid ? urlOrg : (list.length ? list[0].id : "");
        if (initial) {
          setSelectedOrg(initial);
          if (!valid && initial) {
            // Reflect the fallback default in the URL so refresh keeps parity.
            const next = new URLSearchParams(searchParams);
            next.set("org_id", initial);
            setSearchParams(next, { replace: true });
          }
        }
      })
      .catch(() => toast.error("Failed to load organizations"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = useCallback(async (orgId) => {
    if (!orgId) return;
    // Abort any prior in-flight fetch before firing a new one so a slow
    // response for org A can't stomp the UI after we've picked org B.
    if (inflightRef.current.controller) {
      try { inflightRef.current.controller.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    inflightRef.current = { controller, orgId };
    // Clear the table immediately so old rows never linger while loading.
    setRoles([]);
    setLoading(true);
    try {
      const { data } = await API.get(`/roles-permissions/roles/org/${orgId}`, { signal: controller.signal });
      // Stale-response guard: only apply results when the org hasn't changed.
      if (inflightRef.current.orgId !== orgId) return;
      setRoles(normalizeList(data));
    } catch (err) {
      // Axios surfaces cancellation as either code "ERR_CANCELED" or a
      // CanceledError name — either way, swallow silently.
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      if (inflightRef.current.orgId !== orgId) return;
      setRoles([]);
      toast.error(err.response?.data?.detail || "Failed to load roles");
    } finally {
      if (inflightRef.current.orgId === orgId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedOrg) fetchRoles(selectedOrg);
  }, [selectedOrg, fetchRoles]);

  const handleOrgChange = (orgId) => {
    setSelectedOrg(orgId);
    const next = new URLSearchParams(searchParams);
    next.set("org_id", orgId);
    setSearchParams(next, { replace: true });
  };

  const goCreate = () => {
    const qs = selectedOrg ? `?org_id=${encodeURIComponent(selectedOrg)}` : "";
    navigate(`/super-admin/roles-permissions/create${qs}`);
  };

  const goEdit = (rid) => {
    const qs = selectedOrg ? `?org_id=${encodeURIComponent(selectedOrg)}` : "";
    navigate(`/super-admin/roles-permissions/${rid}/edit${qs}`);
  };

  const roleId = (r) => r.id || r.role_id;
  const fundCount = (r) => {
    if (Array.isArray(r.funds)) return r.funds.length;
    const ids = new Set((r.permissions || []).map((p) => p.fund_id).filter(Boolean));
    return ids.size;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roles-permissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Roles &amp; Permissions</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage GL portal roles and their per-module access for each organization.
          </p>
        </div>
        <Button
          onClick={goCreate}
          className="shadow-[0_0_20px_rgba(108,71,196,0.2)]"
          data-testid="create-role-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Role
        </Button>
      </div>

      <Card data-testid="roles-table-card">
        <CardHeader className="p-6 pb-4">
          <div className="w-full sm:w-72 space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Organization</Label>
            <Select value={selectedOrg} onValueChange={handleOrgChange}>
              <SelectTrigger className="bg-secondary/50 h-10" data-testid="roles-org-select">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Role Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Tag</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Funds</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Permissions</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                    No roles found for this organization. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((r) => {
                  // TY-40: prefer role_description, fall back to legacy description
                  // so existing rows keep rendering the current label.
                  const desc = r.role_description || r.description || "";
                  return (
                    <TableRow key={roleId(r)} className="table-row-interactive" data-testid={`role-row-${roleId(r)}`}>
                      <TableCell className="pl-6 font-medium flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-primary" />
                        {r.role_name || r.name || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          {r.role_tag || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="text-muted-foreground text-sm max-w-[280px] truncate"
                        title={desc}
                        data-testid={`role-desc-${roleId(r)}`}
                      >
                        {desc || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fundCount(r)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{(r.permissions || []).length}</TableCell>
                      <TableCell className="text-right pr-6">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => goEdit(roleId(r))}
                          data-testid={`role-edit-${roleId(r)}`}
                        >
                          <Pencil className="w-4 h-4 mr-1.5" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
